import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { ArrowLeft, Flame, Users } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import ProPortalHeader from '@/components/ProPortalHeader';
import { formatApiError } from '@/utils/apiError';

const BRANDS = [{ id: '', label: 'All sectors' }, { id: 'probound', label: 'ProBound' }, { id: 'proepoxy', label: 'ProEpoxy' }, { id: 'prodecor', label: 'ProDecor' }];
const RANGES = [{ v: 90, label: '90 days' }, { v: 365, label: '12 months' }, { v: 0, label: 'All time' }];
const STATUS = {
  gap: { label: 'No installer in range', color: '#dc2626', bg: 'bg-red-50 border-red-200 text-red-800' },
  partial: { label: 'Installer in range, none Approved', color: '#d97706', bg: 'bg-amber-50 border-amber-200 text-amber-800' },
  covered: { label: 'Approved installer in range', color: '#059669', bg: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
};

export default function StaffCoverageGaps() {
  const navigate = useNavigate();
  const [sector, setSector] = useState('');
  const [days, setDays] = useState(365);
  const [data, setData] = useState(null);
  const [show, setShow] = useState({ gap: true, partial: true, covered: false });

  useEffect(() => {
    setData(null);
    fetch(`${API_URL}/api/staff/coverage-gaps?days=${days}${sector ? `&sector=${sector}` : ''}`, { credentials: 'include' })
      .then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(formatApiError(d)); setData(d); })
      .catch((e) => toast.error(e.message));
  }, [sector, days]);

  const points = (data?.points || []).filter((p) => show[p.status]);
  const maxLeads = Math.max(1, ...(data?.points || []).map((p) => p.leads));

  return (
    <div className="min-h-screen bg-[#E8ECEF]">
      <ProPortalHeader activeSection="staff" />
      <main className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4">
        <button onClick={() => navigate('/staff')} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-2" data-testid="gaps-back"><ArrowLeft className="w-4 h-4" /> Staff Dashboard</button>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#27292E] flex items-center gap-2"><Flame className="w-7 h-7 text-red-600" /> Coverage Gaps</h1>
            <p className="text-sm text-slate-600">Where customers are asking for work but no installer will travel — recruit here first.</p>
          </div>
          <button onClick={() => navigate('/staff/installer-map')} className="text-sm font-semibold text-[#006400] underline inline-flex items-center gap-1" data-testid="gaps-to-map"><Users className="w-4 h-4" /> Installer map</button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="inline-flex flex-wrap bg-white border rounded-lg p-0.5" data-testid="gaps-sector">
            {BRANDS.map((b) => <button key={b.id} onClick={() => setSector(b.id)} data-testid={`gaps-sector-${b.id || 'all'}`} className={`px-3 py-1.5 text-sm font-semibold rounded-md ${sector === b.id ? 'bg-[#27292E] text-white' : 'text-gray-600'}`}>{b.label}</button>)}
          </div>
          <div className="inline-flex bg-white border rounded-lg p-0.5" data-testid="gaps-range">
            {RANGES.map((r) => <button key={r.v} onClick={() => setDays(r.v)} data-testid={`gaps-range-${r.v}`} className={`px-3 py-1.5 text-sm font-semibold rounded-md ${days === r.v ? 'bg-[#27292E] text-white' : 'text-gray-600'}`}>{r.label}</button>)}
          </div>
          <div className="flex flex-wrap gap-1.5 ml-auto">
            {Object.entries(STATUS).map(([k, s]) => (
              <button key={k} onClick={() => setShow((x) => ({ ...x, [k]: !x[k] }))} data-testid={`gaps-toggle-${k}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg border ${show[k] ? s.bg : 'bg-white border-gray-200 text-gray-400'}`}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: show[k] ? s.color : '#d1d5db' }} />{s.label}{data ? ` · ${data.summary[k].districts} (${data.summary[k].leads} leads)` : ''}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-3">
          <div className="rounded-xl overflow-hidden border bg-white shadow-sm h-[70vh]" data-testid="gaps-map">
            <MapContainer center={[54.2, -2.5]} zoom={6} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {points.map((p) => (
                <CircleMarker key={p.outcode} center={[p.lat, p.lng]} radius={6 + Math.round(18 * Math.sqrt(p.leads / maxLeads))}
                  pathOptions={{ color: STATUS[p.status].color, fillColor: STATUS[p.status].color, fillOpacity: 0.45, weight: 1.5 }}>
                  <Popup>
                    <div className="text-sm min-w-[190px]">
                      <div className="font-bold text-base">{p.outcode} <span className="text-xs font-normal text-gray-500">postcode district</span></div>
                      <div className="mt-1"><strong>{p.leads}</strong> lead{p.leads === 1 ? '' : 's'}{p.unassigned ? ` · ${p.unassigned} unassigned` : ''}</div>
                      <div className="mt-1 text-xs font-semibold" style={{ color: STATUS[p.status].color }}>{STATUS[p.status].label}</div>
                      <div className="text-xs text-gray-600 mt-1">{p.in_range} installer{p.in_range === 1 ? '' : 's'} in range ({p.in_range_approved} Approved)</div>
                      {p.nearest && <div className="text-xs text-gray-600">Nearest: {p.nearest.name} · {p.nearest.miles} mi</div>}
                      {p.nearest_approved && p.status !== 'covered' && <div className="text-xs text-gray-600">Nearest Approved: {p.nearest_approved.name} · {p.nearest_approved.miles} mi</div>}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
          <aside className="bg-white border rounded-xl shadow-sm h-[70vh] overflow-y-auto" data-testid="gaps-list">
            <div className="px-3 py-2 border-b text-xs font-bold uppercase tracking-wider text-gray-500 sticky top-0 bg-white">Recruitment targets ({points.length})</div>
            {!data && <p className="p-4 text-sm text-gray-500">Crunching leads and installer reach…</p>}
            {data && points.length === 0 && <p className="p-4 text-sm text-gray-500" data-testid="gaps-empty">Nothing to show — {data.points.length === 0 ? 'no leads with a UK postcode in this range.' : 'toggle a status above.'}</p>}
            {points.map((p) => (
              <div key={p.outcode} className="px-3 py-2.5 border-b flex items-center gap-3" data-testid={`gaps-row-${p.outcode}`}>
                <span className="w-3 h-3 rounded-full shrink-0" style={{ background: STATUS[p.status].color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-gray-900">{p.outcode} <span className="text-xs font-normal text-gray-500">· {p.leads} lead{p.leads === 1 ? '' : 's'}</span></div>
                  <div className="text-xs text-gray-500 truncate">{p.nearest ? `Nearest installer ${p.nearest.miles} mi (${p.nearest.name})` : 'No installers located yet'}</div>
                </div>
              </div>
            ))}
          </aside>
        </div>
        <p className="text-[11px] text-gray-500 mt-2">Circle size = number of leads in that postcode district. "In range" uses each installer's travel radius (default 30 mi). Only installers with a postcode on their profile count.</p>
      </main>
    </div>
  );
}
